import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * Page container. Matches `.container` in the design CSS — max-width 1680px,
 * 40px horizontal padding (`--pad-x`), top + bottom breathing room.
 *
 * Most content bands inside the container deliberately break out of this
 * via negative `--pad-x` margins to span edge-to-edge — see `<Band>` below
 * and the .proj-strip / .stat-row / .callout / .recon-group / etc. classes
 * in index.css.
 */
export function Container({ children }: { children: ReactNode }) {
  return <div className="container">{children}</div>;
}

/**
 * `PageHead` matches `.page-head` — eyebrow (smallcaps), title, optional
 * sub line, and an optional right-aligned meta column for "Week ending /
 * Last sync" style label-value pairs.
 *
 * The prior contract split the title into `titleLead` + `titleEm` to
 * carry an italic-blue accent on the trailing phrase; the May 2026
 * design reset killed that ornament, so the prop is now a single `title`.
 */
export interface PageHeadProps {
  eyebrow?: ReactNode;
  title: string;
  sub?: ReactNode;
  meta?: PageMetaRow[];
}

export interface PageMetaRow {
  label: string;
  value: ReactNode;
}

export function PageHead({ eyebrow, title, sub, meta }: PageHeadProps) {
  return (
    <div className="page-head">
      <div>
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h1 className="page-title">{title}</h1>
        {sub && <p className="page-sub">{sub}</p>}
      </div>
      {meta && meta.length > 0 && (
        <div className="page-meta">
          {meta.map((row, i) => (
            <div className="pm-row" key={i}>
              <span className="pm-label">{row.label}</span>
              <span className="pm-val">{row.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Project context strip. Full-bleed band that pins the current project's
 * identity at the top of every page (right under PageHead).
 *
 * Per the handoff README: "Appears on every page (or should — currently
 * only on Dashboard; consider adding to Materials/PCOs/Recon if multi-
 * project becomes a feature)."
 *
 * V1 is single-project Coffman, so we render this on every page — the
 * data is in the auth context (project: {number, name, gc_name}) and
 * we don't have other projects to switch between.
 */
export interface ProjStripProps {
  /** The serif italic em is "·" by convention (a centered dot accent). */
  projectName: string;
  projectNumber: string;
  stats: Array<{ label: string; value: ReactNode; mono?: boolean }>;
}

export function ProjStrip({ projectName, projectNumber, stats }: ProjStripProps) {
  return (
    <div className="proj-strip">
      <div className="ps-name">
        {projectName} <em>·</em>{' '}
        <span className="mono" style={{ fontSize: 14, color: 'var(--ink-3)' }}>
          {projectNumber}
        </span>
      </div>
      {stats.map((s, i) => (
        <div className="ps-stat" key={i}>
          <div className="ps-label">{s.label}</div>
          <div
            className="ps-val"
            style={s.mono === false ? { fontFamily: 'var(--sans)' } : undefined}
          >
            {s.value}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * `Notice` — full-bleed warning/info band that pins to the page width by
 * cancelling `--pad-x` on left and right. Used for "this rollup is out of
 * date" style banners on detail pages. Reconciliation will reuse it when
 * the populated state lands.
 *
 * Variants:
 *   • warning (default) — warning-colored text + ⚠ glyph
 *   • info               — accent-colored text + ℹ glyph
 */
export type NoticeVariant = 'warning' | 'info';

export function Notice({
  variant = 'warning',
  children,
}: {
  variant?: NoticeVariant;
  children: ReactNode;
}) {
  const color =
    variant === 'info' ? 'var(--accent)' : 'var(--warning)';
  const icon = variant === 'info' ? 'ℹ' : '⚠';
  return (
    <div
      style={{
        marginLeft: 'calc(-1 * var(--pad-x))',
        marginRight: 'calc(-1 * var(--pad-x))',
        padding: '14px var(--pad-x)',
        background: 'var(--surface-warm)',
        borderTop: '1px solid var(--rule-strong)',
        borderBottom: '1px solid var(--rule-strong)',
      }}
    >
      <span style={{ color, fontSize: 13, lineHeight: 1.5 }}>
        <span style={{ marginRight: 6 }}>{icon}</span>
        {children}
      </span>
    </div>
  );
}

/**
 * Empty-state placeholder for a pane or section with nothing in it.
 * Hairline-bounded band by default; `flush` drops the band chrome for use
 * inside a container that already centers/frames it (e.g. the inbox detail
 * pane). Optional muted icon above the line; `tone="positive"` colors the
 * icon green for genuinely-good news ("all caught up").
 * No italic — a clean inbox is good news, not poetry.
 */
export function EmptyState({
  eyebrow,
  text,
  icon: Icon,
  tone = 'muted',
  flush = false,
}: {
  eyebrow?: string;
  text: ReactNode;
  icon?: LucideIcon;
  tone?: 'muted' | 'positive';
  flush?: boolean;
}) {
  const cls = [
    'empty-state',
    flush ? 'empty-state--flush' : '',
    tone === 'positive' ? 'empty-state--positive' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls}>
      {Icon && <Icon size={26} strokeWidth={1.5} aria-hidden className="empty-state-ico" />}
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      <div className="empty-state-text">{text}</div>
    </div>
  );
}
